import { Injectable } from '@angular/core';

import { BatchEmission } from './desktop-notification-batch';
import {
  defer,
  drain,
  DeferredInput,
  DeferredState,
  EMPTY_DEFERRED_STATE,
  DESKTOP_DEFERRED_TTL_MS,
} from './desktop-deferred-queue';

/** Transient — deliberately absent from PRESERVED_STORAGE_KEYS, so logout clears it. */
export const DESKTOP_DEFERRED_KEY = 'wolsal.ui.notifications.deferred';

/** Name of the cross-tab lock serialising the drain's read-emit-empty. */
export const DESKTOP_DEFERRED_LOCK = 'volta.notifications.deferred';

/**
 * Holds events that had nowhere to go — the surface was a banner and the user has
 * banners off — in localStorage, so the tab that eventually regains focus is the one
 * that announces them, whichever tab happened to receive the SSE event.
 */
@Injectable({ providedIn: 'root' })
export class DesktopDeferredQueueService {
  /**
   * Holds one event for the user's return.
   *
   * Under the same lock as `drain()`, and for the same reason. The append is a
   * read-modify-write against a store shared by every tab, and `localStorage` has no
   * compare-and-swap: two tabs appending different events at once would both read the
   * same snapshot, both build a new array from it, and the second write would erase the
   * first tab's entry. That loss is silent and permanent — the claim ledger still shows
   * the event as taken, so no tab ever re-queues it, which is precisely the guarantee
   * this queue exists to make.
   *
   * The claim that precedes this call is itself race-free, but it releases before the
   * append runs, so it cannot cover it.
   */
  defer(input: DeferredInput, now: number = Date.now()): Promise<void> {
    const locks = this.lockManager();
    if (!locks) {
      this.takeDefer(input, now);
      return Promise.resolve();
    }
    return locks
      .request(DESKTOP_DEFERRED_LOCK, () => this.takeDefer(input, now))
      .catch(() => this.takeDefer(input, now));
  }

  /**
   * Everything owed, leaving the queue empty. Emptying is a side effect of returning the
   * emission, so two tabs briefly both believing they hold focus would otherwise each
   * read the same queue and each raise the toast. The read-emit-empty therefore runs
   * under a Web Lock, exactly as the claim ledger does and for the same reason:
   * `localStorage` has no compare-and-swap.
   */
  drain(now: number = Date.now()): Promise<BatchEmission | null> {
    const locks = this.lockManager();
    if (!locks) {
      return Promise.resolve(this.takeDrain(now));
    }
    return locks
      .request(DESKTOP_DEFERRED_LOCK, () => this.takeDrain(now))
      .catch(() => this.takeDrain(now));
  }

  /** Whether a drain would announce anything. Read-only, so a tab may ask without consuming. */
  hasPending(now: number = Date.now()): boolean {
    return this.read().entries.some((entry) => now - entry.ts < DESKTOP_DEFERRED_TTL_MS);
  }

  // ── Under the lock ─────────────────────────────────────────────────────────

  private takeDefer(input: DeferredInput, now: number): void {
    this.write(defer(this.read(), now, input));
  }

  private takeDrain(now: number): BatchEmission | null {
    const decision = drain(this.read(), now);
    this.write(decision.state);
    return decision.emission;
  }

  /**
   * Absent before Firefox 96 and Safari 15.4, where the drain stays best-effort and a
   * duplicate toast across two tabs remains possible.
   */
  private lockManager(): LockManager | null {
    return (navigator as Navigator & { locks?: LockManager }).locks ?? null;
  }

  // ── Storage ────────────────────────────────────────────────────────────────

  private read(): DeferredState {
    try {
      const raw = localStorage.getItem(DESKTOP_DEFERRED_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === 'object' && Array.isArray(parsed.entries)) {
        return parsed as DeferredState;
      }
    } catch {
      // fall through to empty state on malformed JSON / disabled storage
    }
    return EMPTY_DEFERRED_STATE;
  }

  private write(state: DeferredState): void {
    try {
      localStorage.setItem(DESKTOP_DEFERRED_KEY, JSON.stringify(state));
    } catch {
      // ignore quota / disabled storage errors — the queue is best-effort
    }
  }
}
